'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import * as React from 'react';
import { Suspense } from 'react';
import { toast } from 'react-hot-toast';
import { EmailVerificationSent } from '@/app/components/auth/EmailVerificationSent';
import { PasswordField } from '@/app/components/auth/PasswordField';
import { AuthLayout } from '@/app/components/auth/AuthLayout';
import { isEmailNotVerifiedError } from '@/lib/auth/authApi';
import { useAuth } from '@/lib/auth/AuthProvider';
import styles from '@/app/components/auth/Auth.module.css';

function resolveReturnUrl(searchParams: ReturnType<typeof useSearchParams>): string {
  const returnUrl = searchParams?.get('returnUrl');
  return returnUrl && returnUrl.startsWith('/') ? returnUrl : '/calendar';
}

function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { state, login } = useAuth();
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = React.useState<string | null>(null);

  const expiredToastShownRef = React.useRef(false);

  React.useEffect(() => {
    if (state.status !== 'authenticated') return;
    router.replace(resolveReturnUrl(searchParams));
  }, [state.status, searchParams, router]);

  React.useEffect(() => {
    if (searchParams?.get('expired') !== '1' || state.status === 'authenticated') return;
    if (expiredToastShownRef.current) return;
    expiredToastShownRef.current = true;
    toast.error('Сессия истекла. Войдите снова.');
    router.replace('/login');
  }, [searchParams, state.status, router]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isEmail = identifier.includes('@');
      await login({
        password,
        ...(isEmail ? { email: identifier } : { username: identifier }),
      });
      toast.success('Вы вошли');
      router.push(resolveReturnUrl(searchParams));
    } catch (err) {
      console.error(err);
      if (isEmailNotVerifiedError(err)) {
        setUnverifiedEmail(identifier.includes('@') ? identifier.trim() : '');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Ошибка входа');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <section className={styles.card}>
        <Link href="/" className={`${styles.close} ${styles.hideOnMobile}`} aria-label="Закрыть">
          ×
        </Link>
        <h1 className={styles.title}>{unverifiedEmail !== null ? 'Подтвердите почту' : 'Вход'}</h1>
        {unverifiedEmail !== null ? (
          <EmailVerificationSent
            email={unverifiedEmail}
            editableEmail={!unverifiedEmail}
            message={
              unverifiedEmail ? (
                <>
                  Почта ещё не подтверждена. Мы отправляли ссылку на{' '}
                  <span className={styles.messageEmail}>{unverifiedEmail}</span>. Перейдите по ней
                  или отправьте письмо ещё раз.
                </>
              ) : (
                'Почта ещё не подтверждена. Укажите адрес, на который регистрировались, и отправьте ссылку ещё раз.'
              )
            }
          />
        ) : state.status === 'authenticated' ? (
          <p className={styles.message}>Вы уже авторизованы. Перенаправляем…</p>
        ) : (
          <form className={styles.form} onSubmit={onSubmit}>
            <label className={styles.field}>
              <span>Логин или эл. почта</span>
              <input
                name="identifier"
                placeholder="email@com"
                autoComplete="username"
                className={styles.input}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </label>
            <PasswordField
              label="Пароль"
              name="password"
              placeholder="Пароль"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className={styles.buttonGroup}>
              <button className={styles.primaryButton} type="submit" disabled={loading}>
                {loading ? 'Входим…' : 'Войти'}
              </button>
              <Link href="/registration" className={styles.secondaryButton}>
                Зарегистрироваться
              </Link>
            </div>
          </form>
        )}

        <Link href="/forgot-password" className={styles.link}>
          Забыли пароль?
        </Link>
      </section>
    </AuthLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageContent />
    </Suspense>
  );
}

